import { useMemo } from "react";
import { EmptyComponent } from "../../../common/EmptyComponent";
import ChatbotEmptyState from "../../components/chatbot-empty-state/chatbot-empty-state";
import ChatbotLayoutWrapper from "../../components/chatbot-layout-wrapper/chatbot-layout-wrapper";
import ChatbotWarningMessage from "../../components/chatbot-warning-message/chatbot-warning-message";
import { ChatbotMessageList } from "../../components/messages/chatbot.message-list";
import UnavailableChatbotMessage from "../../components/unavailable-chatbot-message/unavailable-chatbot-message";
import { CHATBOT_NAME } from "../../../../../features/ai-chatbot/constants/chatbot-name.const";
import { useChatbotPlaceholder } from "../use-chatbot-placeholder/use-chatbot-placeholder";
import type { UseChatbotContentParams } from "./use-chatbot-content.interface";

export function useChatbotContent({
  handleClose,
  handleSubmit,
  handleOnChange,
  handleScrollToBottomClick,
  handleNewSession,
  handleLastChatButtonClick,
  handleOnRetry,
  handleFeedback,
  handleNegativeFeedbackReason,
  isMobile,
  chatInputRef,
  messages,
  showEmptyState = false,
  disabled = false,
  isSending = false,
  isStreamingResponse = false,
  showScrollToBottomButton = false,
  isChatbotAvailableForCurrentUser = true,
  hasLastChatButton = false,
  errorWhileGeneratingResponse = false,
  errorMessage,
  requiredTierForChatbot,
  showRetryButton = true,
  retryButtonText,
}: Readonly<UseChatbotContentParams>) {
  const chatbotPlaceholder = useChatbotPlaceholder({ messages });
  const showWarningMessage = messages?.length === 0 && !showEmptyState;

  const chatbotContent = useMemo(
    function renderChatbotContent() {
      if (!isChatbotAvailableForCurrentUser) {
        return (
          <UnavailableChatbotMessage onClickUpgradeButton={handleClose} requestableFromTier={requiredTierForChatbot} />
        );
      }

      return (
        <>
          {showEmptyState ? (
            <ChatbotEmptyState />
          ) : (
            <ChatbotMessageList
              isSending={isSending}
              isStreamingResponse={isStreamingResponse}
              messages={messages}
              onFeedback={handleFeedback}
              onNegativeFeedbackReason={handleNegativeFeedbackReason}
              isMobile={isMobile}
              hasLastChatButton={hasLastChatButton}
              onClickLastChatButton={handleLastChatButtonClick}
              errorWhileGeneratingResponse={errorWhileGeneratingResponse}
              errorMessage={errorMessage}
              handleOnRetry={handleOnRetry}
              showRetryButton={showRetryButton}
              retryButtonText={retryButtonText}
            />
          )}
          {showWarningMessage ? <ChatbotWarningMessage chatbotName={CHATBOT_NAME} /> : <EmptyComponent />}
        </>
      );
    },
    [
      isChatbotAvailableForCurrentUser,
      isMobile,
      isSending,
      messages,
      showEmptyState,
      showWarningMessage,
      hasLastChatButton,
      errorWhileGeneratingResponse,
      errorMessage,
      requiredTierForChatbot,
      handleLastChatButtonClick,
      handleOnRetry,
      showRetryButton,
      retryButtonText,
      isStreamingResponse,
    ]
  );

  return useMemo(
    function renderChatbotContent() {
      return (
        <ChatbotLayoutWrapper
          chatbotName={CHATBOT_NAME}
          chatbotPlaceholder={chatbotPlaceholder}
          isMobile={isMobile}
          disabled={disabled}
          showScrollToBottomButton={showScrollToBottomButton}
          handleClose={handleClose}
          messages={messages}
          handleNewSession={handleNewSession}
          handleScrollToBottomClick={handleScrollToBottomClick}
          handleOnChange={handleOnChange}
          handleSubmit={handleSubmit}
          chatInputRef={chatInputRef}
          requiredTierForChatbot={requiredTierForChatbot}
          handleFeedback={handleFeedback}
          handleNegativeFeedbackReason={handleNegativeFeedbackReason}
        >
          {chatbotContent}
        </ChatbotLayoutWrapper>
      );
    },
    [
      chatbotPlaceholder,
      isMobile,
      disabled,
      showScrollToBottomButton,
      handleClose,
      messages,
      handleNewSession,
      handleScrollToBottomClick,
      handleOnChange,
      handleSubmit,
      chatInputRef,
      chatbotContent,
      requiredTierForChatbot,
      handleFeedback,
      handleNegativeFeedbackReason,
    ]
  );
}
